/** Нормализация и нечёткое сравнение для поиска (зеркало бэкенд SearchService). */

const STRIP_RE = /[\s\-–—_./\\]+/gu;
const FUZZY_THRESHOLD = 72;
const INVENTORY_SCORE_BOOST = 1000;

/**
 * Регистронезависимо + без пробелов/тире.
 * @param {unknown} value
 * @returns {string}
 */
export function normalizeSearch(value) {
  if (value == null) return '';
  return String(value).toLocaleLowerCase('ru').replace(STRIP_RE, '');
}

/**
 * Разбивает запрос по «+» (логика И: все слова должны совпасть).
 * @param {unknown} query
 * @returns {string[]}
 */
export function splitQueryTerms(query) {
  if (query == null) return [];
  const text = String(query).trim();
  if (!text) return [];
  return text
    .split('+')
    .map((part) => part.trim())
    .filter(Boolean);
}

/**
 * Разделяет термы на текстовые и теговые (#имя).
 * @param {unknown} query
 * @returns {{ textTerms: string[], tagTerms: string[] }}
 */
export function splitTagAndTextTerms(query) {
  const textTerms = [];
  const tagTerms = [];
  for (const term of splitQueryTerms(query)) {
    const stripped = term.trim();
    if (stripped.startsWith('#')) {
      const name = stripped.replace(/^#+/, '').trim();
      if (name) tagTerms.push(name);
    } else {
      textTerms.push(term);
    }
  }
  return { textTerms, tagTerms };
}

/**
 * Запрос для сопоставления с инв. номером (без теговых термов #…).
 * @param {unknown} query
 * @returns {string|null}
 */
export function queryForInventory(query) {
  const { textTerms, tagTerms } = splitTagAndTextTerms(query);
  if (tagTerms.length && !textTerms.length) return null;
  if (textTerms.length) return textTerms.join(' + ');
  const text = query == null ? '' : String(query).trim();
  return text || null;
}

/**
 * Запрос целиком для сравнения с инвентарным номером.
 * @param {unknown} query
 * @returns {string}
 */
function queryAsSingle(query) {
  if (query == null) return '';
  return normalizeSearch(String(query).replace(/\+/g, ' '));
}

/**
 * Расстояние Левенштейна.
 * @param {string} a
 * @param {string} b
 * @returns {number}
 */
function levenshtein(a, b) {
  const m = a.length;
  const n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;
  const prev = new Array(n + 1);
  const curr = new Array(n + 1);
  for (let j = 0; j <= n; j += 1) prev[j] = j;
  for (let i = 1; i <= m; i += 1) {
    curr[0] = i;
    const ca = a.charCodeAt(i - 1);
    for (let j = 1; j <= n; j += 1) {
      const cost = ca === b.charCodeAt(j - 1) ? 0 : 1;
      curr[j] = Math.min(curr[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
    }
    for (let j = 0; j <= n; j += 1) prev[j] = curr[j];
  }
  return prev[n];
}

/**
 * Similarity 0–100 по Левенштейну (аналог ratio).
 * @param {string} a
 * @param {string} b
 * @returns {number}
 */
function ratio(a, b) {
  if (!a && !b) return 100;
  if (!a || !b) return 0;
  const dist = levenshtein(a, b);
  const maxLen = Math.max(a.length, b.length);
  return Math.round((1 - dist / maxLen) * 100);
}

/**
 * partial_ratio: лучший ratio query против окон в text той же длины.
 * @param {string} query
 * @param {string} text
 * @returns {number}
 */
function partialRatio(query, text) {
  if (!query) return 100;
  if (!text) return 0;
  if (text.includes(query)) return 100;
  if (query.length >= text.length) return ratio(query, text);
  let best = 0;
  const len = query.length;
  for (let i = 0; i <= text.length - len; i += 1) {
    const score = ratio(query, text.slice(i, i + len));
    if (score > best) best = score;
    if (best === 100) break;
  }
  const pad = Math.min(2, Math.floor(len / 2));
  for (let extra = 1; extra <= pad; extra += 1) {
    const win = len + extra;
    if (win > text.length) break;
    for (let i = 0; i <= text.length - win; i += 1) {
      const score = ratio(query, text.slice(i, i + win));
      if (score > best) best = score;
    }
  }
  return best;
}

/**
 * Оценка одного терма против blob.
 * @param {string} term
 * @param {string} blob
 * @returns {number}
 */
function scoreTermAgainstBlob(term, blob) {
  const nq = normalizeSearch(term);
  if (!nq) return 100;
  if (!blob) return 0;
  if (blob.includes(nq)) return 100;
  return Math.max(partialRatio(nq, blob), ratio(nq, blob));
}

/**
 * Оценка совпадения с инвентарным номером.
 * @param {string} query
 * @param {unknown} inventoryNumber
 * @returns {number}
 */
export function scoreInventory(query, inventoryNumber) {
  const inv = normalizeSearch(inventoryNumber);
  const nq = queryAsSingle(query);
  if (!nq || !inv) return 0;
  if (nq === inv) return 100;
  if (nq.length >= 4 && (inv.includes(nq) || nq.includes(inv))) return 100;
  return Math.max(partialRatio(nq, inv), ratio(nq, inv));
}

/**
 * Точное совпадение запроса с инвентарным номером (после нормализации).
 * @param {string} query
 * @param {unknown} inventoryNumber
 * @returns {boolean}
 */
export function isExactInventoryMatch(query, inventoryNumber) {
  const inv = normalizeSearch(inventoryNumber);
  const nq = queryAsSingle(query);
  return Boolean(nq) && Boolean(inv) && nq === inv;
}

/**
 * Уверенное совпадение с инв. номером (точное или фрагмент ≥ 4 символов).
 * @param {string} query
 * @param {unknown} inventoryNumber
 * @returns {boolean}
 */
export function isStrongInventoryMatch(query, inventoryNumber) {
  const inv = normalizeSearch(inventoryNumber);
  const nq = queryAsSingle(query);
  if (!nq || !inv) return false;
  if (nq === inv) return true;
  return nq.length >= 4 && (inv.includes(nq) || nq.includes(inv));
}

/**
 * Оценка запроса по текстовым полям и/или тегам (#имя).
 * @param {string} query
 * @param {unknown[]} textParts
 * @param {unknown[]} tagNames
 * @returns {number}
 */
export function scoreArtworkQuery(query, textParts, tagNames = []) {
  const { textTerms, tagTerms } = splitTagAndTextTerms(query);
  if (!textTerms.length && !tagTerms.length) return 100;

  const scores = [];
  if (textTerms.length) {
    const blob = normalizeSearch(
      (Array.isArray(textParts) ? textParts : [])
        .filter((p) => p != null && p !== '')
        .join(' ')
    );
    if (!blob) return 0;
    for (let i = 0; i < textTerms.length; i += 1) {
      const score = scoreTermAgainstBlob(textTerms[i], blob);
      if (score < FUZZY_THRESHOLD) return 0;
      scores.push(score);
    }
  }
  if (tagTerms.length) {
    const tagBlob = normalizeSearch(
      (Array.isArray(tagNames) ? tagNames : [])
        .filter((p) => p != null && p !== '')
        .join(' ')
    );
    if (!tagBlob) return 0;
    for (let i = 0; i < tagTerms.length; i += 1) {
      const score = scoreTermAgainstBlob(tagTerms[i], tagBlob);
      if (score < FUZZY_THRESHOLD) return 0;
      scores.push(score);
    }
  }
  return scores.length ? Math.min(...scores) : 100;
}

/**
 * Оценка совпадения query с одним или несколькими текстовыми фрагментами.
 * Несколько слов через «+» — все должны совпасть (AND); score = минимум по термам.
 * @param {string} query
 * @param {...unknown} parts
 * @returns {number} 0–100
 */
export function scoreSearch(query, ...parts) {
  const terms = splitQueryTerms(query);
  if (!terms.length) return 100;
  const blob = normalizeSearch(parts.filter((p) => p != null && p !== '').join(' '));
  if (!blob) return 0;
  const scores = [];
  for (let i = 0; i < terms.length; i += 1) {
    const score = scoreTermAgainstBlob(terms[i], blob);
    if (score < FUZZY_THRESHOLD) return 0;
    scores.push(score);
  }
  return scores.length ? Math.min(...scores) : 100;
}

/**
 * Фильтрация произведений: приоритет инвентарного номера, при точном совпадении — один результат.
 * @param {Array<object>} artworks
 * @param {string} query
 * @param {(a: object) => unknown[]} getBlobParts — поля для общего поиска
 * @returns {Array<object>}
 */
export function filterArtworksBySearch(artworks, query, getBlobParts) {
  if (!query || !String(query).trim()) return artworks;
  const items = Array.isArray(artworks) ? artworks : [];
  const invQuery = queryForInventory(query);

  if (invQuery) {
    const exact = items.filter((a) => isExactInventoryMatch(invQuery, a.inventory_number));
    if (exact.length) {
      exact.sort((a, b) => (a.id || 0) - (b.id || 0));
      return exact.slice(0, 1);
    }

    const strong = items.filter((a) => isStrongInventoryMatch(invQuery, a.inventory_number));
    if (strong.length) {
      strong.sort((a, b) => {
        const ds = scoreInventory(invQuery, b.inventory_number) - scoreInventory(invQuery, a.inventory_number);
        if (ds !== 0) return ds;
        return (a.id || 0) - (b.id || 0);
      });
      return strong;
    }
  }

  const scored = [];
  for (const a of items) {
    const invScore = invQuery ? scoreInventory(invQuery, a.inventory_number) : 0;
    if (invScore >= FUZZY_THRESHOLD) {
      scored.push({ artwork: a, score: invScore + INVENTORY_SCORE_BOOST });
      continue;
    }
    const parts = typeof getBlobParts === 'function' ? getBlobParts(a) : [];
    const tagNames = (a.tags || []).map((t) => (typeof t === 'string' ? t : t.name));
    const blobScore = scoreArtworkQuery(query, parts, tagNames);
    if (blobScore >= FUZZY_THRESHOLD) {
      scored.push({ artwork: a, score: blobScore });
    }
  }

  scored.sort((x, y) => {
    if (y.score !== x.score) return y.score - x.score;
    return (x.artwork.id || 0) - (y.artwork.id || 0);
  });
  return scored.map((row) => row.artwork);
}

/**
 * @param {string} query
 * @param {...unknown} parts
 * @returns {boolean}
 */
export function textMatchesSearch(query, ...parts) {
  return scoreSearch(query, ...parts) >= FUZZY_THRESHOLD;
}
